import { inject, Injectable } from '@angular/core';
import { AdyenCheckout, Core, Dropin, components, PaymentCompletedData, PaymentFailedData, AdyenCheckoutError } from '@adyen/adyen-web';
import { catchError, map, Observable, of } from 'rxjs';
import { AdyenValidatePayload, MastercardPayload } from '../models/payment.model';
import { PayAtTableNiPayload } from '../models/pay-at-table.model';
import { ApiService } from './api.service';
// Adyen Web v6 no longer auto-registers payment method components with Drop-in.
// Only register methods that complete on this page (Card/Apple Pay/Google Pay/WeChat
// Pay Web all resolve via onPaymentCompleted/onPaymentFailed without leaving it).
// Methods that require a full top-level redirect (PayPal, Klarna, bank redirects, ...)
// aren't supported yet: the return trip lands on /bill-paid, which has no logic to
// resume the Adyen session and read redirectResult from the URL. Registering one of
// those would let a shopper pay without the transaction ever being validated.
const supportedComponents = [components.Card, components.ApplePay, components.GooglePay, components.WeChat];
Core.register(...supportedComponents);

export interface AdyenSessionDetails {
  clientKey: string;
  environment: 'test' | 'live';
  transactionId: string;
  session: {
    id: string;
    sessionData: string;
  };
}

export interface AdyenValidateParams {
  sessionId: string;
  transactionId?: string | null;
  redirectResult?: string | null;
  sessionResult?: string | null;
  chequeId?: string | null;
}

export interface AdyenDropinHandlers {
  onPaymentCompleted: (resultCode: string, sessionResult: string | null) => void;
  onPaymentFailed: (resultCode: string, sessionResult: string | null) => void;
  onError: (error: AdyenCheckoutError) => void;
}

function getSessionResult(data: PaymentCompletedData | PaymentFailedData): string | null {
  return 'sessionResult' in data ? data.sessionResult : null;
}

@Injectable({
  providedIn: 'root',
})
export class AdyenService {
  private apiService = inject(ApiService);

  private checkout: Core | null = null;
  private dropin: Dropin | null = null;

  public getSessionDetails(payload: MastercardPayload | PayAtTableNiPayload): Observable<AdyenSessionDetails> {
    return this.apiService.getAdyenSession(payload).pipe(
      map((session) => ({
        clientKey: session.client_key,
        environment: session.environment,
        transactionId: session.transaction_id,
        session: {
          id: session.id,
          sessionData: session.sessionData,
        },
      }))
    );
  }

  public async mount(containerId: string, sessionDetails: AdyenSessionDetails, handlers: AdyenDropinHandlers): Promise<void> {
    this.checkout = await AdyenCheckout({
      environment: sessionDetails.environment,
      clientKey: sessionDetails.clientKey,
      session: sessionDetails.session,
      onPaymentCompleted: (data: PaymentCompletedData) => handlers.onPaymentCompleted(data.resultCode, getSessionResult(data)),
      onPaymentFailed: (data: PaymentFailedData) => handlers.onPaymentFailed(data.resultCode, getSessionResult(data)),
      onError: (error: AdyenCheckoutError) => handlers.onError(error),
    });

    this.dropin = new Dropin(this.checkout, {}).mount(`#${containerId}`);
  }

  public unmount(): void {
    this.dropin?.unmount();
    this.dropin = null;
    this.checkout = null;
  }

  validateAdyenSession(params: AdyenValidateParams): Observable<boolean> {
    const payload: AdyenValidatePayload = {
      session_id: params.sessionId,
      transaction_id: params.transactionId ?? null,
      redirect_result: params.redirectResult ?? '',
      session_result: params.sessionResult ?? null,
    };
    if (params.chequeId) {
      payload.cheque_id = params.chequeId;
    }

    return this.apiService.validateAdyenTransaction(payload).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }
}
